/**
 * `03/CRF/Confirmation Entry` (38:1144) — `/countries/:cc/crfs/:crfId/confirmation`.
 *
 * The per-point grid: the customer's decision on every main point of one CRF,
 * entered by an operator from whatever the customer actually said.
 *
 * ===========================================================================
 * OQ-A6 — THE BUTTON RECORDS; IT DOES NOT SEND
 * ===========================================================================
 * No customer email exists in this system (`sendCrfConfirmation`'s own header:
 * STATE ONLY, owner-ruled). The design's label for this action is "Resend
 * mail", which claims a transport that is not there — so the button says
 * "Record send" (first time) / "Record reminder" (after), and the toast says
 * the whole truth verbatim: "Recorded. No customer mail is sent yet —
 * transport awaits an owner ruling." A recorded deviation, not a drift.
 *
 * ===========================================================================
 * THE GRID REPLACES WHOLE, AND THE SAVE BUTTON SAYS SO
 * ===========================================================================
 * `PUT /crfs/:id/points` renumbers rows 1..N from array order (`putCrfPoints`).
 * The save button carries `title="Saving rewrites all points"` so the claim is
 * visible where the click happens.
 *
 * ===========================================================================
 * PENDING IS THE ABSENCE OF A CHOICE
 * ===========================================================================
 * A pending row renders with NO segment selected (`POINT_DECISIONS`
 * deliberately has three entries, not four). Clicking a segment selects it;
 * clicking the selected one leaves it selected — the server's `pending` means
 * "never decided", and offering un-deciding here would let the client
 * manufacture a state the workflow cannot mean.
 *
 * ===========================================================================
 * THE REMARK RULE, BOTH HALVES
 * ===========================================================================
 * Client half: `pointNeedsRemark` (domain.ts) blocks the save and badges the
 * offending rows — red ring on the remark input, count in a footer sentence.
 * Server half: a 422 whose details name `REMARK_REQUIRED` badges EXACTLY the
 * rows the server named (`remarkRequiredPoints`), because the server's list is
 * the ruling even when the client's mirror would have passed.
 *
 * D5: a viewer without `crf.edit` sees the decisions as PILLS (not buttons),
 * no remark inputs, no save footer and no send action — absent, not disabled.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Alert, Button, Card, Chip, CountryBadge, SkeletonBlock } from '../../components/ui';
// WT5 — `inquiries/panel` died with the slide-over kill; the property rows
// relocated verbatim to `record-furniture` (import repoint only, no restyle).
import { PropertyGroup, PropertyRow } from '../inquiries/record-furniture';
import { useToast } from '../inquiries/toast';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { CRF_STAGES, POINT_DECISIONS, pointNeedsRemark } from './domain';
import { crfErrorText, remarkRequiredPoints } from './errors';
import { crfKeys, fetchCrf, putCrfPoints, sendCrfConfirmation } from './api';
import type { CrfPointDto } from './api';

type Decision = CrfPointDto['confirmation'];

interface PointRow {
  point_no: number;
  description: string;
  confirmation: Decision;
  remark: string;
}

/* ───────────────────────────────────────────── stage + decision treatments ── */

type CrfTone = 'violet' | 'amber' | 'blue' | 'green' | 'slate' | 'red';

/**
 * CRF_STAGES tones → EXISTING token pairs only (domain.ts: "no new colors are
 * invented here"). violet is the `stage/confirm` pair — the design's own
 * violet; the rest ride the `status/*` family and the neutral chip treatment.
 */
const TONE_PILL: Readonly<Record<CrfTone, string>> = {
  violet: 'bg-stage-confirm-bg text-stage-confirm-fg',
  amber: 'bg-warn-bg text-warn-fg',
  blue: 'bg-info-bg text-info-fg',
  green: 'bg-ok-bg text-ok-fg',
  slate: 'bg-subtle text-fg-secondary ring-1 ring-line',
  red: 'bg-danger-bg text-danger-fg',
};

/**
 * The 3px record spine, light `…-fg` / dark `…-bg` — the flip `domain/quotes.ts`
 * RAIL_CLASSES measured (saturated fg tokens sink on the dark surface).
 */
const TONE_RAIL: Readonly<Record<CrfTone, string>> = {
  violet: 'bg-stage-confirm-fg dark:bg-stage-confirm-bg',
  amber: 'bg-warn-fg dark:bg-warn-bg',
  blue: 'bg-info-fg dark:bg-info-bg',
  green: 'bg-ok-fg dark:bg-ok-bg',
  slate: 'bg-fg-secondary',
  red: 'bg-danger-fg dark:bg-danger-bg',
};

function CrfStagePill({ status }: { status: string }): React.JSX.Element {
  const descriptor = CRF_STAGES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        descriptor === undefined
          ? 'bg-subtle text-fg-secondary ring-1 ring-line'
          : TONE_PILL[descriptor.tone],
      )}
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      {descriptor?.label ?? status}
    </span>
  );
}

/** Read-only decision pill — the viewer's rendering of one row's state. */
const DECISION_PILL: Readonly<Record<Decision, { label: string; classes: string }>> = {
  confirmed: { label: 'Confirmed', classes: 'bg-ok-bg text-ok-fg' },
  not_confirmed: { label: 'Not confirmed', classes: 'bg-danger-bg text-danger-fg' },
  on_hold: { label: 'On hold', classes: 'bg-warn-bg text-warn-fg' },
  pending: { label: 'Pending', classes: 'bg-subtle text-fg-secondary ring-1 ring-line' },
};

function DecisionPill({ decision }: { decision: Decision }): React.JSX.Element {
  const { label, classes } = DECISION_PILL[decision];
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        classes,
      )}
    >
      {label}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────── screen ── */

export function ConfirmationEntryPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { crfId } = useParams<{ crfId: string }>();
  const { can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canView = can(countryCode, 'crf.view');
  const canEdit = can(countryCode, 'crf.edit');

  const id = crfId ?? '';
  const record = useQuery({
    queryKey: crfKeys.record(id),
    queryFn: ({ signal }) => fetchCrf(id, signal),
    enabled: canView && id !== '',
  });

  /*
   * The editable grid. `draft` is keyed to the record it was edited FROM, so a
   * different CRF (or a fresh server answer after save resets it to null) never
   * shows another record's half-typed rows. Derived rather than seeded by an
   * effect: there is no render in which `rows` and the payload can disagree
   * about which record they describe.
   */
  const [draft, setDraft] = useState<{ forId: string; rows: readonly PointRow[] } | null>(null);
  /** Point numbers the CLIENT gate flagged at the last blocked save attempt. */
  const [clientFlagged, setClientFlagged] = useState<readonly number[]>([]);

  const crf = record.data;
  const serverRows: readonly PointRow[] = (crf?.points ?? []).map((point) => ({
    point_no: point.point_no,
    description: point.description,
    confirmation: point.confirmation,
    remark: point.remark ?? '',
  }));
  const rows: readonly PointRow[] =
    draft !== null && crf !== undefined && draft.forId === crf.id ? draft.rows : serverRows;

  const saveMutation = useMutation({
    mutationFn: () =>
      putCrfPoints(id, {
        points: rows.map(({ description, confirmation, remark }) => ({
          description,
          confirmation,
          remark: remark.trim() === '' ? null : remark,
        })),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(crfKeys.record(id), updated);
      setDraft(null);
      setClientFlagged([]);
      toast.push({ text: 'Confirmation saved.' });
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => sendCrfConfirmation(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(crfKeys.record(id), updated);
      // OQ-A6 — EXACTLY honest, first send and reminder alike. See the header.
      toast.push({
        text: 'Recorded. No customer mail is sent yet — transport awaits an owner ruling.',
      });
    },
    onError: (error) => {
      toast.push({ text: crfErrorText(error), tone: 'error' });
    },
  });

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="danger" title="This confirmation is not available to this account">
          Viewing CRFs in {countryCode} needs the CRF permission.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonBlock className="h-4 w-64" />
        <SkeletonBlock className="h-8 w-80" />
        <SkeletonBlock className="h-64 w-full" />
      </div>
    );
  }

  if (record.isError || crf === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This CRF could not be opened">
          {crfErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/crfs/confirmations`}
          className="w-fit text-body13 text-indigo hover:underline"
        >
          Back to Work Confirmation
        </Link>
      </div>
    );
  }

  const descriptor = CRF_STAGES[crf.status];
  const total = rows.length;
  const confirmedCount = rows.filter((row) => row.confirmation === 'confirmed').length;
  const progressText = `${String(confirmedCount)} of ${String(total)} confirmed`;

  /** Rows the SERVER named in its last REMARK_REQUIRED refusal — the ruling. */
  const serverFlagged = remarkRequiredPoints(saveMutation.error);

  const isBadged = (row: PointRow): boolean =>
    serverFlagged.includes(row.point_no) ||
    (clientFlagged.includes(row.point_no) && pointNeedsRemark(row.confirmation, row.remark));

  const badgedCount = rows.filter(isBadged).length;

  const updateRow = (pointNo: number, patch: Partial<Pick<PointRow, 'confirmation' | 'remark'>>): void => {
    setDraft({
      forId: crf.id,
      rows: rows.map((row) => (row.point_no === pointNo ? { ...row, ...patch } : row)),
    });
    // An edit is a new statement — the server's last refusal (and its row
    // badges) described the payload BEFORE this keystroke.
    if (saveMutation.isError) saveMutation.reset();
  };

  const onSave = (): void => {
    const offending = rows
      .filter((row) => pointNeedsRemark(row.confirmation, row.remark))
      .map((row) => row.point_no);
    if (offending.length > 0) {
      setClientFlagged(offending);
      return;
    }
    setClientFlagged([]);
    saveMutation.mutate();
  };

  const sendLabel = crf.confirmation_sent_at === null ? 'Record send' : 'Record reminder';

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* The record header, with the 3px stage spine bleeding into the page's
       * left margin — the InquiryRecordPage arrangement, displacing nothing. */}
      <header className="relative flex items-stretch">
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 -left-[15px] w-[3px] shrink-0',
            TONE_RAIL[descriptor?.tone ?? 'slate'],
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <p className="text-label text-fg-muted">
            <Link to={`/countries/${countryCode}/crfs`} className="hover:underline">
              CRFs
            </Link>
            {' / '}
            <span className="font-mono text-mono">{crf.doc_no}</span>
          </p>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <CountryBadge code={countryCode} />
              <h1 className="truncate text-h1 text-fg">{crf.party_name ?? 'Unknown customer'}</h1>
              <CrfStagePill status={crf.status} />
            </div>

            {/* D5 — absent for a caller without `crf.edit`, and absent once the
             * CRF has left draft: past confirmation, re-recording a send would
             * backdate a state the workflow already consumed. */}
            {canEdit && crf.status === 'draft' ? (
              <Button
                variant="primary"
                disabled={sendMutation.isPending}
                onClick={() => sendMutation.mutate()}
              >
                {sendLabel}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <Card
            title="Confirmation"
            action={<Chip className="tnum">{progressText}</Chip>}
          >
            <p className="text-label text-fg-secondary">
              {`${String(total)} ${total === 1 ? 'point' : 'points'} · each needs a customer decision`}
            </p>

            {total === 0 ? (
              <p className="text-body13 text-fg-muted">This CRF has no points yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <caption className="sr-only">Customer decision per point for {crf.doc_no}</caption>
                  <thead>
                    <tr className="border-b border-line">
                      <th scope="col" className="w-12 px-3 py-2.5 text-left text-th uppercase text-fg-secondary">
                        SR
                      </th>
                      <th scope="col" className="px-3 py-2.5 text-left text-th uppercase text-fg-secondary">
                        Main point
                      </th>
                      <th scope="col" className="px-3 py-2.5 text-left text-th uppercase text-fg-secondary">
                        Status
                      </th>
                      <th scope="col" className="w-[240px] px-3 py-2.5 text-left text-th uppercase text-fg-secondary">
                        Remark
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const badged = isBadged(row);
                      return (
                        <tr key={row.point_no} className="border-b border-line align-top last:border-b-0">
                          <td className="px-3 py-3">
                            <span className="tnum font-mono text-mono text-fg-secondary">
                              {String(row.point_no).padStart(2, '0')}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-body13 text-fg">{row.description}</td>
                          <td className="px-3 py-3">
                            {canEdit ? (
                              <div
                                role="group"
                                aria-label={`Decision for point ${String(row.point_no)}`}
                                className="flex w-fit items-center gap-1 rounded-sm border border-line bg-subtle p-1"
                              >
                                {POINT_DECISIONS.map((decision) => {
                                  const selected = row.confirmation === decision.key;
                                  return (
                                    <button
                                      key={decision.key}
                                      type="button"
                                      aria-pressed={selected}
                                      // Re-clicking the selected key re-selects it —
                                      // no unset-by-click (file header).
                                      onClick={() =>
                                        updateRow(row.point_no, { confirmation: decision.key })
                                      }
                                      className={cn(
                                        'whitespace-nowrap rounded-sm px-2.5 py-1 text-label',
                                        selected
                                          ? 'bg-surface font-medium text-fg shadow-tab-active'
                                          : 'text-fg-secondary hover:text-fg',
                                      )}
                                    >
                                      {decision.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <DecisionPill decision={row.confirmation} />
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {canEdit ? (
                              <input
                                type="text"
                                value={row.remark}
                                maxLength={300}
                                placeholder="Add remark…"
                                aria-label={`Remark for point ${String(row.point_no)}`}
                                aria-invalid={badged}
                                onChange={(event) =>
                                  updateRow(row.point_no, { remark: event.target.value })
                                }
                                className={cn(
                                  'h-9 w-full min-w-[200px] rounded-sm border bg-subtle px-3 text-body text-fg outline-none placeholder:text-fg-muted',
                                  badged
                                    ? 'border-danger-fg ring-1 ring-danger-fg'
                                    : 'border-line',
                                )}
                              />
                            ) : row.remark === '' ? (
                              <span className="text-body13 text-fg-muted">—</span>
                            ) : (
                              <span className="text-body13 text-fg-secondary">{row.remark}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* The design's footer rule, verbatim (38:8234). */}
            <p className="text-label text-fg-muted">
              A remark is required whenever a point is not confirmed or put on hold.
            </p>

            {badgedCount > 0 ? (
              <p className="text-label font-medium text-danger-fg">
                {badgedCount === 1
                  ? '1 point still needs a remark before this can be saved.'
                  : `${String(badgedCount)} points still need a remark before this can be saved.`}
              </p>
            ) : null}

            {saveMutation.isError ? (
              <Alert tone="danger" title="The confirmation could not be saved">
                {crfErrorText(saveMutation.error)}
              </Alert>
            ) : null}
          </Card>

          {/* D5 — the whole footer is absent for a viewer, not disabled. */}
          {canEdit ? (
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 flex items-center justify-between gap-3 border-t border-line bg-surface px-6 py-3.5">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <span className="tnum text-label text-fg-secondary">{progressText}</span>
              <Button
                variant="primary"
                title="Saving rewrites all points"
                disabled={saveMutation.isPending || total === 0}
                onClick={onSave}
              >
                Save confirmation
              </Button>
            </div>
          ) : null}
        </div>

        {/* `Record/PropertiesPanel` at 320 — the InquiryRecordPage arrangement. */}
        <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[320px]">
          <div className="flex flex-col gap-5 rounded-md border border-line bg-surface p-5">
            <PropertyGroup label="Confirmation">
              <PropertyRow name="Sent on">
                {crf.confirmation_sent_at === null
                  ? 'Not sent yet'
                  : formatDateLong(crf.confirmation_sent_at)}
              </PropertyRow>
              {/* "Sent by" — absent-if-null, and the record DTO carries no
               * sender field at all (api.ts), so the row never renders rather
               * than rendering a guess. */}
              <PropertyRow name="Reminders">
                {`${String(crf.confirmation_reminders)} ${
                  crf.confirmation_reminders === 1 ? 'reminder' : 'reminders'
                } sent`}
              </PropertyRow>
            </PropertyGroup>

            <PropertyGroup label="System">
              <PropertyRow name="CRF ID" mono>
                {crf.doc_no}
              </PropertyRow>
              <PropertyRow name="Created">{formatDateLong(crf.created_at)}</PropertyRow>
            </PropertyGroup>
          </div>

          <div className="rounded-md border border-line bg-subtle p-4 text-label text-fg-secondary">
            Customer confirms each point before development starts.
          </div>
        </aside>
      </div>
    </div>
  );
}
